import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAdminFetchApi from '../../utils/adminFetchApi';

export default function AdminSpotPrices() {
  const api = useAdminFetchApi();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [channelFilter, setChannelFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Create form state (append-only)
  const [form, setForm] = useState({ spot_price: '', currency: 'USD', channel: 'global' });

  // Inline drafts and saving tracking
  const [drafts, setDrafts] = useState({}); // { [id]: { spot_price?, currency?, channel?, created_at? } }
  const [savingIds, setSavingIds] = useState(new Set());

  const clearMessages = () => { setError(''); setInfo(''); };

  const fetchList = useCallback(async () => {
    setLoading(true);
    clearMessages();
    try {
      const params = new URLSearchParams();
      if (channelFilter) params.set('channel', channelFilter);
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      const data = await api.get(`/admin/spot_prices?${params.toString()}`);
      const payload = data?.data || data || {};
      const rows = Array.isArray(payload.items) ? payload.items : (Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
      setItems(rows);
      const t = (payload.total ?? data?.total ?? 0);
      if (typeof t === 'number') setTotal(t);
    } catch (e) {
      setError(e.message || 'Failed to load spot prices');
    } finally {
      setLoading(false);
    }
  }, [api, channelFilter, limit, offset]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const canPrev = offset > 0;
  const canNext = useMemo(() => offset + limit < (total || 0), [offset, limit, total]);
  const prevPage = () => setOffset(prev => Math.max(0, prev - limit));
  const nextPage = () => setOffset(prev => prev + limit);

  const onChangeForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    clearMessages();
    const raw = (form.spot_price ?? '').toString().trim();
    if (raw === '' || isNaN(Number(raw))) {
      setError('Spot price is required and must be numeric');
      return;
    }
    const payload = {
      spot_price: Number(raw),
      currency: (form.currency || 'USD').trim() || 'USD',
      channel: (form.channel || 'global').trim() || 'global',
    };
    try {
      const resp = await api.post('/admin/spot_prices', payload);
      setInfo(resp?.message || 'Spot price added successfully');
      setForm({ spot_price: '', currency: payload.currency, channel: payload.channel });
      // Refresh to include new entry
      setOffset(0); // go to first page where newest likely appears
      fetchList();
    } catch (e) {
      setError(e.message || 'Failed to add spot price');
    }
  };

  const getDraft = (id) => drafts[id] || {};
  const setDraft = (id, changes) => setDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...changes } }));

  const commitInline = async (rowId, changes) => {
    if (!rowId) return;
    // optimistic update
    setItems(prev => prev.map(r => (r.id === rowId ? { ...r, ...changes } : r)));
    setSavingIds(prev => { const n = new Set(prev); n.add(rowId); return n; });
    try {
      await api.put(`/admin/spot_prices/${rowId}`, changes);
    } catch (e) {
      setError(e.message || 'Update failed');
      // reload row to be safe
      try {
        const one = await api.get(`/admin/spot_prices/${rowId}`);
        const row = one?.data || one;
        setItems(prev => prev.map(r => (r.id === rowId ? row : r)));
      } catch (_) {}
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(rowId); return n; });
      setDrafts(prev => { const n = { ...prev }; delete n[rowId]; return n; });
    }
  };

  const renderCell = (row, key) => {
    const saving = savingIds.has(row.id);
    if (key === 'spot_price') {
      const d = getDraft(row.id);
      const val = d.spot_price != null ? d.spot_price : (row.spot_price ?? '');
      return (
        <input
          type="number"
          step="0.01"
          inputMode="decimal"
          value={val}
          disabled={saving}
          onChange={(e)=>setDraft(row.id, { spot_price: e.target.value })}
          onBlur={()=>{
            const vv = (val ?? '').toString().trim();
            if (vv === '' || isNaN(Number(vv))) return; // ignore invalid
            commitInline(row.id, { spot_price: Number(vv) });
          }}
          onKeyDown={(e)=>{ if (e.key==='Enter'){ const vv = (val ?? '').toString().trim(); if (vv!=='' && !isNaN(Number(vv))) commitInline(row.id, { spot_price: Number(vv) }); } if (e.key==='Escape') setDrafts(prev=>{ const n={...prev}; delete n[row.id]; return n; }); }}
          style={{ width: 140 }}
        />
      );
    }
    if (key === 'currency') {
      const d = getDraft(row.id);
      const val = d.currency != null ? d.currency : (row.currency || 'USD');
      return (
        <input
          type="text"
          value={val}
          disabled={saving}
          onChange={(e)=>setDraft(row.id, { currency: e.target.value })}
          onBlur={()=>commitInline(row.id, { currency: (val || 'USD').trim() || 'USD' })}
          onKeyDown={(e)=>{ if (e.key==='Enter') commitInline(row.id, { currency: (val || 'USD').trim() || 'USD' }); if (e.key==='Escape') setDrafts(prev=>{ const n={...prev}; delete n[row.id]; return n; }); }}
          style={{ width: 120 }}
        />
      );
    }
    if (key === 'channel') {
      const d = getDraft(row.id);
      const val = d.channel != null ? d.channel : (row.channel || 'global');
      return (
        <input
          type="text"
          value={val}
          disabled={saving}
          onChange={(e)=>setDraft(row.id, { channel: e.target.value })}
          onBlur={()=>commitInline(row.id, { channel: (val || 'global').trim() || 'global' })}
          onKeyDown={(e)=>{ if (e.key==='Enter') commitInline(row.id, { channel: (val || 'global').trim() || 'global' }); if (e.key==='Escape') setDrafts(prev=>{ const n={...prev}; delete n[row.id]; return n; }); }}
          style={{ width: 160 }}
        />
      );
    }
    if (key === 'created_at') {
      // Display nicely, allow correction via text/datetime-local if desired
      const d = getDraft(row.id);
      const raw = d.created_at != null ? d.created_at : (row.created_at || '');
      const display = (() => { try { const dt = new Date(raw); return isNaN(dt.getTime()) ? String(raw || '') : dt.toLocaleString(); } catch (_) { return String(raw || ''); } })();
      return (
        <span title={String(raw || '')}>{display}</span>
      );
    }
    return row[key] ?? '';
  };

  const columns = useMemo(() => ['id','spot_price','currency','channel','created_at'], []);

  return (
    <div className="admin-section">
      <div className="admin-toolbar">
        <div className="field">
          <label>Channel filter</label>
          <input type="text" value={channelFilter} onChange={(e)=>{ setChannelFilter(e.target.value); setOffset(0);} } placeholder="global | iraq | malaysia" />
        </div>
        <div className="field">
          <label>Page size</label>
          <select value={limit} onChange={(e)=>{ setLimit(parseInt(e.target.value,10)); setOffset(0); }}>
            {[25,50,100,200,500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="spacer" />
        <div className="pagination">
          <button onClick={prevPage} disabled={!canPrev || loading}>Prev</button>
          <span className="page-hint">offset {offset} {total?`/ ${total}`:''}</span>
          <button onClick={nextPage} disabled={!canNext || loading}>Next</button>
        </div>
        <button onClick={fetchList} disabled={loading} className="submit-button" style={{width:'auto'}}>Refresh</button>
      </div>

      {error && <div className="error-message" style={{marginBottom:12}}>{error}</div>}
      {info && <div className="error-message" style={{marginBottom:12, background:'#12321e', color:'#c3f8ca', borderColor:'#14532d'}}>{info}</div>}

      <div className="panel" style={{marginBottom:12}}>
        <h3 style={{marginTop:0}}>Add spot price</h3>
        <form onSubmit={submitCreate} className="grid-2">
          <div className="field">
            <label>Spot price</label>
            <input type="number" step="0.01" inputMode="decimal" value={form.spot_price} onChange={(e)=>onChangeForm('spot_price', e.target.value)} placeholder="e.g., 6.90" />
          </div>
          <div className="field">
            <label>Currency</label>
            <input type="text" value={form.currency} onChange={(e)=>onChangeForm('currency', e.target.value)} placeholder="USD" />
          </div>
          <div className="field">
            <label>Channel</label>
            <input type="text" value={form.channel} onChange={(e)=>onChangeForm('channel', e.target.value)} placeholder="global | iraq | malaysia" />
          </div>
          <div style={{display:'flex', alignItems:'flex-end'}}>
            <button type="submit" className="button primary">Add</button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="loading-row">Loading spot prices…</div>
      ) : (
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {columns.map(col => <th key={col}>{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={columns.length} style={{textAlign:'center', color:'#94a3b8'}}>No spot prices</td></tr>
              ) : items.map(row => (
                <tr key={row.id}>
                  {columns.map(col => (
                    <td key={col}>{renderCell(row, col)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
